import {
	type CSSProperties,
	Children,
	type ReactElement,
	type ReactNode,
	cloneElement,
	useMemo,
	useRef,
} from 'react'
import ReactDOM from 'react-dom'

import { SheetContent } from './SheetContent/SheetContent'
import { SheetOverlay } from './SheetOverlay/SheetOverlay'
import { useSheet } from './hooks/useSheet/useSheet'
import { getMouseY } from './utils/mouse'

import styles from './Sheet.module.scss'

const WINDOW_PART_TO_CLOSE = 0.25 // 1 / 4 of window height
const TOP_OFFSET = 64 // px

const extendProps =
	(
		child: ReactNode,
		props: {
			// Close function for the children
			closeEvent: (event: { [key: string]: string }) => void
		}
	) =>
	() =>
		cloneElement(Children.only(child as ReactElement), props)

function getTopFromContainer(container: HTMLDivElement | null) {
	return container?.getBoundingClientRect().top ?? 0
}

function getHeightFromContainer(container: HTMLDivElement | null) {
	return container?.clientHeight ?? 0
}

function shouldClose(difference: number) {
	return (
		difference >= 0 &&
		Math.abs(difference) > window.innerHeight * WINDOW_PART_TO_CLOSE
	)
}

function getHeightStyle(height: number, offset: number) {
	return {
		maxHeight: height ? '100dvh' : `calc(90dvh - ${TOP_OFFSET}px)`,
		height: height ? `${height}px` : 'auto',
		'--offset-dragging': offset,
	} as CSSProperties
}

export type SheetProps = {
	readonly open: boolean
	readonly onClose: () => void
	readonly name: string
	readonly body: ReactNode
	// Bubble, component to appear above the Sheet body (use case: size guide link)
	readonly bubble?: ReactNode
	readonly header?: ReactNode
	readonly footer?: ReactNode
	readonly 'data-testid'?: string
}
/**
 * This Component is deprecated to use, but we cannot deprecated
 * with the notation deprecated because sizeSheet still using this component
 * and the new one it is not ready yet from UX point of view.
 *
 * TODO: Remove this component when the new one is ready
 */
export function Sheet({
	open,
	onClose,
	name,
	body,
	bubble,
	header,
	footer,
	'data-testid': dataTestId,
}: SheetProps) {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const headerRef = useRef<HTMLDivElement | null>(null)
	const footerRef = useRef<HTMLDivElement | null>(null)
	const offsetRef = useRef<number>(0)
	const lastRef = useRef<number>(0)
	const isDraggingRef = useRef<boolean>(false)

	const {
		opened,
		offset,
		height,
		contentHeight,
		footerMargin,
		restoreHeight,
		setHeight,
		setOffset,
	} = useSheet(open, containerRef, headerRef, footerRef, isDraggingRef)

	function storeInitialMouseOffset(pageY: number) {
		const top = getTopFromContainer(containerRef?.current)
		offsetRef.current = pageY - top
		lastRef.current = pageY
	}

	function onDrop(e: TouchEvent) {
		if (shouldClose(getMouseY(e) - lastRef.current)) {
			onClose()
		} else {
			setHeight(restoreHeight)
		}
		isDraggingRef.current = false
		window.removeEventListener('touchmove', onDragging)
		window.removeEventListener('touchend', onDrop)
	}

	function onDragging(e: TouchEvent) {
		const delta = window.innerHeight - getMouseY(e) + offsetRef.current
		isDraggingRef.current = true
		if (delta < window.innerHeight) {
			setHeight(delta)
			setOffset(delta - getHeightFromContainer(containerRef.current))
		}
	}

	function onDragStart(e: React.TouchEvent<HTMLDivElement>) {
		e.stopPropagation()
		storeInitialMouseOffset(getMouseY(e))
		window.addEventListener('touchmove', onDragging)
		window.addEventListener('touchend', onDrop)
	}

	/**
	 * When the content scrolls to top and user keep dragging
	 */
	function contentOnTop(pageY: number) {
		// Remove previously listener
		window.removeEventListener('touchmove', onDragging)
		window.removeEventListener('touchend', onDrop)
		storeInitialMouseOffset(pageY)
		window.addEventListener('touchmove', onDragging)
		window.addEventListener('touchend', onDrop)
	}

	const propsToExtend = {
		// Close function for the children
		closeEvent: onClose,
	}

	const headerMemo = useMemo(() => extendProps(header, propsToExtend), [header])
	const bubbleMemo = useMemo(() => extendProps(bubble, propsToExtend), [bubble])
	const footerMemo = useMemo(() => extendProps(footer, propsToExtend), [footer])
	const bodyMemo = useMemo(() => extendProps(body, propsToExtend), [body])

	const HeaderComponent = headerMemo
	const BubbleComponent = bubbleMemo
	const FooterComponent = footerMemo
	const BodyComponent = bodyMemo

	return opened
		? ReactDOM.createPortal(
				<div
					id={name}
					className={styles.wrapper}
					aria-labelledby='sheet-title'
					aria-describedby='sheet-description'
					data-testid={dataTestId}
				>
					<SheetOverlay onClose={onClose} />
					<div
						ref={containerRef}
						onTouchStart={onDragStart}
						data-testid='sheet.draggable.dialog'
						style={getHeightStyle(height, offset)}
						className={styles.main}
					>
						{bubble && (
							<div className={styles.bubble}>
								<BubbleComponent />
							</div>
						)}
						<div className={styles.draggable}>
							<span className={styles.bar} />
						</div>
						{header && (
							<div ref={headerRef} className={styles.header}>
								<HeaderComponent />
							</div>
						)}
						<SheetContent
							parentHeight={contentHeight}
							footerMargin={footerMargin}
							onTop={contentOnTop}
						>
							<BodyComponent />
						</SheetContent>
						{footer && (
							<div ref={footerRef} className={styles.footer}>
								<FooterComponent />
							</div>
						)}
					</div>
				</div>,
				document.body
			)
		: null
}
